import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/App';
import { toast } from 'sonner';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PaymentSuccess = ({ onUpdate }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('error');
      return;
    }

    try {
      const response = await apiClient.get(`/payment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Paiement réussi ! Votre abonnement est actif.');
        onUpdate && onUpdate();
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('La session de paiement a expiré');
      } else {
        // Continuer à vérifier
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const goToDashboard = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-slate-400 mx-auto animate-spin mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Vérification du paiement...</h1>
            <p className="text-slate-500">Veuillez patienter pendant que nous confirmons votre paiement.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Paiement réussi !</h1>
            <p className="text-slate-500 mb-6">
              Votre abonnement est maintenant actif. Vous avez accès à toutes les fonctionnalités premium.
            </p>
            <Button onClick={goToDashboard} className="w-full bg-slate-900 text-white">
              Retour au tableau de bord
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Erreur de vérification</h1>
            <p className="text-slate-500 mb-6">
              Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, votre abonnement sera activé automatiquement.
            </p>
            <Button onClick={goToDashboard} className="w-full bg-slate-900 text-white">
              Retour au tableau de bord
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
